import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignature } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@progress/kendo-react-buttons';
import type { FieldTypeDefinition, FieldRendererProps } from '../registry';

const CANVAS_W = 400;
const CANVAS_H = 150;

function SignatureRenderer({ value, onChange, readOnly }: FieldRendererProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasContent, setHasContent] = useState(false);

  // Restore a saved signature onto the canvas when the field mounts
  useEffect(() => {
    if (typeof value !== 'string' || !value.startsWith('data:')) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => { ctx.clearRect(0, 0, CANVAS_W, CANVAS_H); ctx.drawImage(img, 0, 0); };
    img.src = value;
    setHasContent(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function scaledPos(e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    // CSS display size may differ from the canvas pixel buffer — scale accordingly
    const sx = CANVAS_W / rect.width;
    const sy = CANVAS_H / rect.height;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * sx, y: (clientY - rect.top) * sy };
  }

  function startDraw(e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
    if (readOnly) return;
    drawing.current = true;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const { x, y } = scaledPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function draw(e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
    if (!drawing.current || readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000';
    const { x, y } = scaledPos(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  function endDraw() {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current!;
    onChange(canvas.toDataURL('image/png'));
    setHasContent(true);
  }

  function clear() {
    const canvas = canvasRef.current!;
    canvas.getContext('2d')!.clearRect(0, 0, CANVAS_W, CANVAS_H);
    setHasContent(false);
    onChange(null);
  }

  if (readOnly && typeof value === 'string' && value.startsWith('data:')) {
    return (
      <img
        src={value}
        alt="Signature"
        style={{
          display: 'block',
          maxWidth: '100%',
          border: '1px solid var(--kendo-color-border)',
          borderRadius: 'var(--kendo-border-radius-md)',
        }}
      />
    );
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        style={{
          display: 'block',
          maxWidth: '100%',
          height: CANVAS_H,
          border: '1px solid var(--kendo-color-border)',
          borderRadius: 'var(--kendo-border-radius-md)',
          background: '#fff',
          cursor: readOnly ? 'default' : 'crosshair',
          touchAction: 'none',
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      {!readOnly && (
        <Button
          size="small"
          fillMode="flat"
          disabled={!hasContent}
          onClick={clear}
          style={{ marginTop: 'var(--kendo-spacing-1)' }}
        >
          Clear
        </Button>
      )}
    </div>
  );
}

export const definition: FieldTypeDefinition = {
  key: 'SIG',
  label: 'Signature',
  category: 'advanced',
  icon: <FontAwesomeIcon icon={faSignature} fixedWidth />,
  defaultProps: { label: 'Signature', isRequired: false },
  Renderer: SignatureRenderer,
};
